/**
 * BUSINESS FORM BODY — Campi del form azienda, riusabili.
 *
 * Stesso pattern di PersonFormBody:
 *   - BusinessForm (modale standalone create/edit)
 *   - AddRelationDrawer tab "Crea nuova" (crea + collega)
 *
 * I bottoni submit/cancel li mette il parent nel footer della modale,
 * con attribute form={formId} per triggerare il submit di questo form.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import TextField from '@mui/material/TextField';
import { type BusinessCreatePayload, BUSINESS_FORM_DEFAULTS } from './businessTypes';
import { CountrySelect } from '../../components/CountrySelect';
import { RegionSelect } from '../../components/RegionSelect';

interface BusinessFormBodyProps {
  formId: string;
  initialValues?: BusinessCreatePayload;
  onSubmit: (payload: BusinessCreatePayload) => Promise<void>;
}

export function BusinessFormBody({ formId, initialValues, onSubmit }: BusinessFormBodyProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<BusinessCreatePayload>(initialValues ?? BUSINESS_FORM_DEFAULTS);
  // Regola "almeno uno tra nome e dominio": vincolo cross-field non esprimibile
  // come `required` sul singolo campo, quindi validato qui al submit.
  const [nameError, setNameError] = useState(false);

  useEffect(() => {
    if (initialValues) {
      setForm(initialValues);
      setNameError(false);
    }
  }, [initialValues]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    // Almeno uno tra nome e dominio deve essere valorizzato.
    if (form.name.trim().length === 0 && form.domain.trim().length === 0) {
      setNameError(true);
      return;
    }
    setNameError(false);
    await onSubmit(form);
  }

  return (
    <form id={formId} onSubmit={handleSubmit}>
      {/* Il codice esterno lo scrive il gestionale, non l'utente: si mostra, se c'è, e non si cambia. */}
      {form.externalCode && (
        <TextField label={t('business.externalCode')} value={form.externalCode} disabled fullWidth className="form-field" />
      )}
      <TextField label={t('business.companyName')} value={form.name} onChange={(e) => { setForm({ ...form, name: e.target.value }); if (nameError) setNameError(false); }} error={nameError} fullWidth className="form-field" />
      <TextField label={t('business.domainName')} value={form.domain} onChange={(e) => { setForm({ ...form, domain: e.target.value }); if (nameError) setNameError(false); }} error={nameError} helperText={nameError ? t('business.validation.nameOrDomainRequired') : undefined} fullWidth className="form-field" />
      {/* Email e telefono: campo singolo (il contatto PRIMARIO). La semantica di
          persistenza è decisa dal consumer in handleSubmit — CREATE: nuovo
          contatto primario; EDIT (BusinessForm): update del primario esistente,
          o add se assente. */}
      <TextField type="email" label={t('business.emailPrimary')} value={form.email ?? ''} onChange={(e) => setForm({ ...form, email: e.target.value })} fullWidth className="form-field" />
      <TextField type="tel" label={t('business.phonePrimary')} value={form.phone ?? ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} fullWidth className="form-field" />
      <TextField label={t('business.owner')} value={form.owner ?? ''} onChange={(e) => setForm({ ...form, owner: e.target.value })} fullWidth className="form-field" />
      <TextField label={t('business.sector')} value={form.sector ?? ''} onChange={(e) => setForm({ ...form, sector: e.target.value })} fullWidth className="form-field" />
      <TextField label={t('business.type')} value={form.type ?? ''} onChange={(e) => setForm({ ...form, type: e.target.value })} fullWidth className="form-field" />
      <TextField label={t('business.employees')} value={form.employees ?? ''} onChange={(e) => setForm({ ...form, employees: e.target.value })} fullWidth className="form-field" />
      <TextField label={t('business.vatNumber')} value={form.vatNumber ?? ''} onChange={(e) => setForm({ ...form, vatNumber: e.target.value })} fullWidth className="form-field" slotProps={{ htmlInput: { maxLength: 20 } }} />
      <TextField label={t('business.fiscalCode')} value={form.fiscalCode ?? ''} onChange={(e) => setForm({ ...form, fiscalCode: e.target.value })} fullWidth className="form-field" slotProps={{ htmlInput: { maxLength: 16 } }} />
      <TextField label={t('business.sdiCode')} value={form.sdiCode ?? ''} onChange={(e) => setForm({ ...form, sdiCode: e.target.value })} fullWidth className="form-field" slotProps={{ htmlInput: { maxLength: 10 } }} />
      {/* Testo e non type="email": la PEC ha solo il controllo di lunghezza, come in scheda e nelle API (TS-142, D1). inputMode tiene la tastiera email sul mobile. */}
      <TextField label={t('business.pecEmail')} value={form.pecEmail ?? ''} onChange={(e) => setForm({ ...form, pecEmail: e.target.value })} fullWidth className="form-field" slotProps={{ htmlInput: { maxLength: 254, inputMode: 'email' } }} />
      {/* Indirizzo (opzionale): stesso pattern di email/phone ma multi-field.
          Persistenza in BusinessForm.handleSubmit con dispatch separato di
          addAddress. Raggruppato visivamente con <fieldset> per separare la
          sezione dai campi scalari del Business. */}
      <fieldset className="form-fieldset">
        <legend>{t('business.addressPrimary')}</legend>
        <TextField label={t('address.name')} value={form.addressName ?? ''} onChange={(e) => setForm({ ...form, addressName: e.target.value })} fullWidth className="form-field" />
        <TextField label={t('address.description')} value={form.addressDescription ?? ''} onChange={(e) => setForm({ ...form, addressDescription: e.target.value })} fullWidth className="form-field" />
        <TextField label={t('address.street')} value={form.addressStreet ?? ''} onChange={(e) => setForm({ ...form, addressStreet: e.target.value })} fullWidth className="form-field" />
        <TextField label={t('address.city')} value={form.addressCity ?? ''} onChange={(e) => setForm({ ...form, addressCity: e.target.value })} fullWidth className="form-field" />
        <TextField label={t('address.zip')} value={form.addressZip ?? ''} onChange={(e) => setForm({ ...form, addressZip: e.target.value })} fullWidth className="form-field" />
        {/* Country/Region: lookup da slice geography. Reset region on
            country change gestito qui nel parent: l'onChange di CountrySelect
            azzera anche addressRegionId. */}
        <CountrySelect
          value={form.addressCountryId}
          onChange={(countryId) => setForm({ ...form, addressCountryId: countryId, addressRegionId: null })}
          fullWidth
          className="form-field"
        />
        <RegionSelect
          value={form.addressRegionId}
          onChange={(regionId) => setForm({ ...form, addressRegionId: regionId })}
          countryId={form.addressCountryId}
          fullWidth
          className="form-field"
        />
      </fieldset>
    </form>
  );
}
